import React, { useState } from 'react';
import { useTools } from '../hooks/useTools';

export const Compare = () => {
  const { data: tools, isLoading, error } = useTools();
  const [selectedToolIds, setSelectedToolIds] = useState<number[]>([]);

  const handleToolSelect = (toolId: number) => {
    if (selectedToolIds.includes(toolId)) {
      setSelectedToolIds(selectedToolIds.filter(id => id !== toolId));
    } else if (selectedToolIds.length < 2) {
      setSelectedToolIds([...selectedToolIds, toolId]);
    }
  };

  const selectedTools = tools?.filter(tool => selectedToolIds.includes(tool.id)) || [];

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-8">Compare AI Writing Tools</h1>
      
      {isLoading ? (
        <div className="flex items-center justify-center min-h-[200px]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : error ? (
        <div className="text-red-500 p-4 bg-red-50 rounded">
          Error loading tools: {error.message}
        </div>
      ) : (
        <>
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4">Select up to 2 tools to compare:</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {tools?.map(tool => (
                <button
                  key={tool.id}
                  onClick={() => handleToolSelect(tool.id)}
                  className={`p-4 rounded-lg border transition-colors ${
                    selectedToolIds.includes(tool.id)
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <h3 className="font-semibold text-lg">{tool.name}</h3>
                  <p className="text-sm text-gray-600 mt-2">{tool.description}</p>
                </button>
              ))}
            </div>
          </div>

          {selectedTools.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className="border p-4 bg-gray-50">Comparison</th>
                    {selectedTools.map(tool => (
                      <th key={tool.id} className="border p-4 bg-gray-50">
                        {tool.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border p-4 font-semibold">Description</td>
                    {selectedTools.map(tool => (
                      <td key={tool.id} className="border p-4">
                        {tool.description}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="border p-4 font-semibold">Key Features</td>
                    {selectedTools.map(tool => (
                      <td key={tool.id} className="border p-4">
                        <ul className="list-disc list-inside space-y-2">
                          {tool.key_features?.split(';').map((feature, index) => (
                            <li key={index} className="text-sm">
                              {feature.trim()}
                            </li>
                          ))}
                        </ul>
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="border p-4 font-semibold">Use Cases</td>
                    {selectedTools.map(tool => (
                      <td key={tool.id} className="border p-4">
                        {tool.use_cases}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="border p-4 font-semibold">Pricing</td>
                    {selectedTools.map(tool => (
                      <td key={tool.id} className="border p-4">
                        {tool.pricing}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="border p-4 font-semibold">Supported Languages</td>
                    {selectedTools.map(tool => (
                      <td key={tool.id} className="border p-4">
                        {tool.supported_languages}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="border p-4 font-semibold">Comparison Criteria</td>
                    {selectedTools.map(tool => (
                      <td key={tool.id} className="border p-4">
                        {tool.comparison_criteria}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <td className="border p-4 font-semibold">Links</td>
                    {selectedTools.map(tool => (
                      <td key={tool.id} className="border p-4 space-y-2">
                        <div>
                          <a 
                            href={tool.website_url || '#'} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-500 hover:text-blue-700"
                          >
                            Official Website
                          </a>
                        </div>
                        {tool.affiliate_link && (
                          <div>
                            <a 
                              href={tool.affiliate_link} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-500 hover:text-blue-700"
                            >
                              Affiliate Program
                            </a>
                          </div>
                        )}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Compare; 